import { Button } from '@aecom-ai-engineering/react-components';
import clsx from 'clsx';
import { ArrowDown } from 'lucide-react';
import { useCallback, useLayoutEffect, useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { DocumentFullscreenContainer } from '@/features/document-viewer/fullscreen/document/container';
import { ViewerMode, useViewerContext } from '@/features/document-viewer/use-viewer-context';
import { type Message } from '@/util/interfaces';
import { prefersReducedMotion } from '@/util/prefers-reduced-motion';

import { PropChatInitialState } from './prop-chat-initial-state';
import { ChatInput } from './prop-chat-input';
import { ChatMessages } from './prop-chat-messages';
import { type PropChatVariant } from './types';

/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- The independently scrollable conversation must receive keyboard focus. */

interface PropChatProps {
  loading: boolean;
  loadingMessage: boolean;
  disabled: boolean;
  isResumedChat: boolean;
  messages: Message[];
  onMessage: (message: Message) => void;
  variant: PropChatVariant;
}

const SCROLL_BOTTOM_THRESHOLD = 24;

export const PropChat: FC<PropChatProps> = ({
  loading,
  loadingMessage,
  disabled,
  isResumedChat,
  messages,
  onMessage,
  variant,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldFollowLatestRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const { t } = useTranslation();
  const { hasFullscreenHost, mode } = useViewerContext();
  const isFullscreen = variant === 'fullscreen';
  const isInitialChat = !isResumedChat && messages.length === 0 && !loading && !disabled;
  // Only a fallback, for screens with no viewer of their own. Rendering
  // unconditionally stacked a second fullscreen viewer on top of the one the
  // screen already had — most visibly on Findings, which uses its own
  // DocumentViewerDrawer.
  const documentViewer =
    mode === ViewerMode.DocumentFullscreen && !hasFullscreenHost ? (
      <DocumentFullscreenContainer />
    ) : null;

  const updateScrollPosition = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const distanceFromBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
    const isAwayFromBottom = distanceFromBottom > SCROLL_BOTTOM_THRESHOLD;

    shouldFollowLatestRef.current = !isAwayFromBottom;
    setShowScrollToBottom(isAwayFromBottom);
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const resolvedBehavior = prefersReducedMotion() ? 'auto' : behavior;

    if (resolvedBehavior === 'smooth' && scrollContainer.scrollTo) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: resolvedBehavior });
    } else {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
    shouldFollowLatestRef.current = true;
    setShowScrollToBottom(false);
  }, []);

  // The conversation also grows without any of this component's props
  // changing - a step's minimum spinner time expiring swaps a short wait for a
  // full answer, and the thinking steps animate open - so the layout effect
  // below never runs for those and the view is left short of the bottom.
  // Watching the content itself covers every such growth, including late
  // arrivals like images and web fonts.
  const contentObserver = useRef<ResizeObserver | null>(null);
  const observeContent = useCallback(
    (node: HTMLDivElement | null) => {
      contentObserver.current?.disconnect();
      contentObserver.current = null;

      if (!node || typeof ResizeObserver === 'undefined') return;

      // Only ever follows: growth while the user is reading further up must not
      // drag them back down.
      const observer = new ResizeObserver(() => {
        if (shouldFollowLatestRef.current) scrollToBottom();
      });
      observer.observe(node);
      contentObserver.current = observer;
    },
    [scrollToBottom],
  );

  const sendMessage = useCallback(
    (message: Message) => {
      // Sending re-engages following wherever the user had scrolled to. Without
      // this the layout effect below bails out, and someone who had scrolled up
      // to re-read something would send a question and see nothing happen.
      scrollToBottom();
      onMessage(message);
    },
    [onMessage, scrollToBottom],
  );

  useLayoutEffect(() => {
    if (!shouldFollowLatestRef.current) {
      updateScrollPosition();
      return;
    }

    scrollToBottom();

    // A second pass next frame, because the answer grows after layout and the
    // first scroll lands short. Re-checked rather than unconditional: an answer
    // updates every frame, so there is always a queued pass, and an
    // unconditional one both yanked the user back the moment they scrolled away
    // and — since scrollToBottom re-engages following — undid the scroll that
    // disengaged it. Between the two, scrolling away during an answer was
    // impossible.
    const animationFrame = requestAnimationFrame(() => {
      if (shouldFollowLatestRef.current) scrollToBottom();
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [loading, loadingMessage, messages, scrollToBottom, updateScrollPosition, variant]);

  return (
    <div className="ds-body-font flex h-full w-full flex-col bg-[var(--ds-color-bg-primary)]">
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollContainerRef}
          data-prop-chat-scroll
          role="log"
          aria-label={t('prop-chat.conversation')}
          // Announcements are handled by ChatMessages once an answer is
          // complete. Leaving the log itself live would re-announce the whole
          // answer on every streamed character, since each one re-renders the
          // markdown body.
          aria-live="off"
          aria-busy={loading || loadingMessage}
          tabIndex={0}
          className={clsx(
            'flex h-full min-h-0 flex-col overflow-y-auto overscroll-contain focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--ds-color-border-secondary)]',
            isInitialChat ? 'p-0' : 'px-[var(--ds-space-4)] py-[var(--ds-space-4)]',
          )}
          onScroll={updateScrollPosition}
        >
          {isInitialChat ? (
            <PropChatInitialState />
          ) : (
            <div
              ref={observeContent}
              className={clsx(
                isFullscreen && 'mx-auto w-full max-w-[640px]',
                // The states that stand alone rather than sitting above a
                // conversation are centred in the viewport, not stacked at the
                // top of an empty scroll area.
                (disabled || loading) && 'flex min-h-full flex-col justify-center',
              )}
            >
              <ChatMessages
                variant={variant}
                loading={loading}
                loadingMessage={loadingMessage}
                disabled={disabled}
                messages={messages}
              />
            </div>
          )}
        </div>
        {showScrollToBottom && !isInitialChat && (
          <Button
            aria-label={t('prop-chat.scroll-to-bottom')}
            variant="ghost"
            size="mini"
            className="absolute bottom-[var(--ds-space-3)] left-1/2 size-[var(--ds-space-8)] min-h-0 -translate-x-1/2 rounded-full border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-[var(--ds-space-2)] text-[var(--ds-color-text-secondary)] shadow-md hover:border-[var(--ds-color-border-secondary)] hover:bg-[var(--ds-color-bg-primary)]"
            onClick={() => scrollToBottom('smooth')}
          >
            <ArrowDown aria-hidden="true" className="size-[var(--ds-space-4)]" />
          </Button>
        )}
      </div>

      <ChatInput
        variant={variant}
        disabled={disabled}
        loading={loading}
        loadingMessage={loadingMessage}
        onMessage={sendMessage}
      />
      {documentViewer ? createPortal(documentViewer, document.body) : null}
    </div>
  );
};
